<template>
	<div
		class="hdl-short-text-response question"
		:class="{
			'highlight-required': highlightRequired
		}"
	>
		<div ref="rendered" v-html="question.renderedQuestion" />
		<input
			type="text"
			:value="(response)? response : ''"
			@input="input"
			tabindex=0
			class="input-box-borders"
			ref="textbox"
			@keydown="keydownHandler"
		/>
	</div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { PublishedSurveyQuestionType, SurveyResponseErrorCode, SurveyResponseInputErrorType } from '@/common/interfaces';

import { IQuestionComponent } from './types';
import { ResponseGroup } from '../../responses';
import { useResponseStore } from '@/respondants/stores/responseStore';

@Component({
	emits: ['intentionalBlur']
})
export default class ShortTextResponse extends Vue implements IQuestionComponent {
	@Prop({ required: true })
	question: PublishedSurveyQuestionType;

	response: string | null;

	highlightRequired: boolean = false; /* Visually accentuate the fact that this question is required */

	mounted(): void {
		this.$el.__vue__ = this;

		if (this.responseGroup) {
			const existingResponse = this.responseGroup.getResponse(this.question.uuid);
			this.response = (existingResponse)? existingResponse.text : null;
		}

		this.accessorizeRendered();
	}

	focusFrom(fromDirection: 'above' | 'below'): boolean {
		const textbox = this.$refs.textbox as HTMLInputElement | undefined;

		if (textbox) {
			textbox.focus();
		}

		return true;
	}

	keydownHandler(event: KeyboardEvent): void {
		if (event.key === 'ArrowDown') {
			event.stopPropagation();
			event.preventDefault();

			this.$emit('intentionalBlur', this.$el, this.question, 'down');
		} else if (event.key == 'ArrowUp') {
			event.stopPropagation();
			event.preventDefault();

			this.$emit('intentionalBlur', this.$el, this.question, 'up');
		} else if (event.key === 'Enter') {
			event.stopPropagation();
			event.preventDefault();

			this.$emit('intentionalBlur', this.$el, this.question, 'down');
		}
	}

	accessorizeRendered(): void {
		const rendered = this.$refs.rendered as HTMLDivElement | undefined;

		if (rendered === undefined) {
			return;
		}

		const label = rendered.querySelector(`label[for="${this.question.uuid}"]`);

		if (label !== null) {
			const labelTail = document.createElement('div');
			labelTail.classList.add('tail');
			label.appendChild(labelTail);

			if (this.question.required) {
				const requiredIndicator = document.createElement('div');
				requiredIndicator.classList.add('required-indicator');
				requiredIndicator.textContent = '✱';
				labelTail.appendChild(requiredIndicator);
			}
		}
	}

	getResponse(): string | null {
		return this.response;
	}

	hasResponse(): boolean {
		return this.response !== null;
	}

	input(event: Event): void {
		this.response = (event.target as HTMLInputElement).value;
		this.highlightRequired = false;

		if (this.responseGroup) {
			this.responseGroup.setResponse(this.question.uuid, this.response, null, null);
		}
	}

	markError(error: SurveyResponseInputErrorType): void {
		if (error.code === SurveyResponseErrorCode.MISSING_REQUIRED) {
			this.highlightRequired = true;
		}
	}

	get responseGroup(): ResponseGroup | null {
		return this.responseStore.responseGroup;
	}

	get responseStore() {
		return useResponseStore();
	}
}
</script>

<style lang="scss">
.hdl-short-text-response {
	&.highlight-required {
		label {
			@apply text-hdl-red;
		}
	}

	input[type="text"] {
		@apply rounded w-full bg-fill;

		padding: .35rem .5rem;

		&:focus {
			outline: none;
			--tw-border-opacity: 1;
			border: 1.5px solid var(--main);
			box-shadow: #535abb25 0 0 0 2px;
		}
	}
}
</style>
